// Pure derivation of the POS V2 payments box: which payments of an order are
// shown as rows, each row's share for THIS order, and which layout mode the
// box is in (single compact tile vs. expandable list).
//
// Share rule (mirrors the server and pos-sync): a payment covering several
// orders stores each order's share in paymentReferences[].amount; explicit 0
// means "this payment funded nothing of that order". A null share on a
// multi-order payment carries no allocation data (legacy rows only), so the
// payment is neither shown nor refundable as this order's money — a missing
// row beats a wrong amount with a live refund. A null share on a single-order
// payment is the whole payment.
import { paymentMethodIcon } from '@/domain/payments/payment-method-icon';
import { OrderPaymentStatus } from '@/enums/OrderEnums';
import { PaymentEntityType } from '@/enums/PaymentEntityType';
import { PaymentStatus, PaymentType } from '@/enums/PaymentEnums';
import { CajaPaymentMethod } from '@/interfaces/ICaja';
import { IPayment } from '@/interfaces/IPayment';
import { roundPrice } from '@/utils';
import { paymentMethodToCajaMethodOrNull } from '@/utils/orders.utils';

export type TPaymentsBoxMode =
  | 'loading'
  | 'error'
  | 'plain'
  | 'single'
  | 'list';

export interface IPaymentsBoxRow {
  id: number;
  paymentType: PaymentType;
  /** Label resolved by the component: posV2.method.<bucket> or posV2.methodOther. */
  bucket: CajaPaymentMethod | null;
  /** PosUiIcon name. */
  icon: string;
  /** THIS order's share of the payment. */
  amount: number;
  /** The payment also covers other orders (share < payment total). */
  shared: boolean;
  createdAt: string;
  reimbursed: boolean;
}

export interface IPaymentsBoxState {
  mode: TPaymentsBoxMode;
  /** order.totalPaid coerced. */
  paid: number;
  /** roundPrice(total - totalPaid), not clamped (parity with the legacy line). */
  toPay: number;
  /** Newest first, includes reimbursed rows. */
  rows: IPaymentsBoxRow[];
  /** Rows not reimbursed. */
  activeCount: number;
  /**
   * Sum of the active rows' shares. Equals `paid` once rows and totals come
   * from the same committed state; a mismatch means the rows are stale.
   */
  activeTotal: number;
  /** Unique buckets of active rows, in row order. */
  buckets: Array<CajaPaymentMethod | null>;
}

/**
 * The server now emits the order update (new totals) after the payment
 * transaction commits, so the reload it triggers normally reads the final
 * rows. This one re-read stays as defense for mixed-version deploys and any
 * remaining in-transaction emitter: when the rows that land disagree with the
 * totals, a single retry after this delay settles it while the previous rows
 * stay on screen.
 */
export const PAYMENTS_BOX_RECONCILE_DELAY_MS = 1500;

// Decimals and dates arrive as strings over JSON on some paths; the inputs
// admit both so callers (and specs) never need to cast.
export interface IPaymentsBoxOrder {
  id: number;
  paymentStatus: OrderPaymentStatus;
  total: number | string;
  totalPaid: number | string | null | undefined;
}

export type TPaymentsBoxPayment = Pick<
  IPayment,
  'id' | 'type' | 'status' | 'paymentMethod' | 'paymentReferences'
> & {
  total: number | string;
  createdAt: Date | string;
};

const ROW_STATUSES: ReadonlySet<PaymentStatus> = new Set([
  PaymentStatus.Paid,
  PaymentStatus.Reimbursed,
]);

function toRow(
  payment: TPaymentsBoxPayment,
  orderId: number
): IPaymentsBoxRow | null {
  if (
    payment.type !== PaymentType.Order &&
    payment.type !== PaymentType.ReservationDeposit
  )
    return null;
  if (!ROW_STATUSES.has(payment.status)) return null;

  const total = roundPrice(+payment.total || 0);
  const orderRefs =
    payment.paymentReferences?.filter(
      (r) => r.entityType === PaymentEntityType.Order
    ) ?? [];
  const ref = orderRefs.find((r) => r.entityId === orderId);
  // A reservation deposit is account money only after an explicit allocation.
  if (payment.type === PaymentType.ReservationDeposit && ref?.amount == null)
    return null;
  // No allocation data for THIS order on a multi-order payment: cannot be
  // shown or refunded as this order's money (see the share rule above).
  if (ref?.amount == null && orderRefs.length > 1) return null;
  const amount = ref?.amount == null ? total : roundPrice(+ref.amount);
  // Explicit 0 share: this payment did not fund this order.
  if (amount <= 0) return null;

  const bucket = paymentMethodToCajaMethodOrNull(payment.paymentMethod);
  const createdMs = new Date(payment.createdAt).getTime();
  return {
    id: payment.id,
    paymentType: payment.type,
    bucket,
    icon: paymentMethodIcon(bucket),
    amount,
    shared: amount < total,
    createdAt: Number.isFinite(createdMs)
      ? new Date(createdMs).toISOString()
      : '',
    reimbursed: payment.status === PaymentStatus.Reimbursed,
  };
}

export function paymentsBoxRowLabelKey(
  row: Pick<IPaymentsBoxRow, 'paymentType' | 'bucket'>
): string {
  return row.paymentType === PaymentType.ReservationDeposit
    ? 'posV2.method.reservationDeposit'
    : row.bucket
    ? `posV2.method.${row.bucket}`
    : 'posV2.methodOther';
}

/** Deposits must be refunded through their reservation accounting workflow. */
export function canRefundPaymentsBoxRow(
  row: Pick<IPaymentsBoxRow, 'paymentType' | 'reimbursed'>
): boolean {
  return !row.reimbursed && row.paymentType !== PaymentType.ReservationDeposit;
}

function timeOf(iso: string): number {
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function compareNewestFirst(a: IPaymentsBoxRow, b: IPaymentsBoxRow): number {
  const byDate = timeOf(b.createdAt) - timeOf(a.createdAt);
  // Same-second ties are common (group pay, quick re-tenders); MySQL returns
  // them in arbitrary order, so the id keeps the list stable across reloads.
  return byDate !== 0 ? byDate : b.id - a.id;
}

export function getPaymentsBoxState(params: {
  order: IPaymentsBoxOrder;
  /** null = no rows landed yet for this order (first load / order switch). */
  payments: TPaymentsBoxPayment[] | null;
  loadError: boolean;
  /**
   * A reload for the current totals is in flight and `payments` are the
   * previous rows. Stale rows that disagree with the totals must not be
   * presented as the definitive single tile (they get a list tile instead).
   */
  refetching?: boolean;
}): IPaymentsBoxState {
  const { order, payments, loadError, refetching = false } = params;
  const paid = roundPrice(+(order.totalPaid ?? 0) || 0);
  const toPay = roundPrice((+order.total || 0) - paid);

  const rows = (payments ?? [])
    .map((payment) => toRow(payment, order.id))
    .filter((row): row is IPaymentsBoxRow => row !== null)
    .sort(compareNewestFirst);

  const activeRows = rows.filter((row) => !row.reimbursed);
  const activeTotal = roundPrice(
    activeRows.reduce((sum, row) => sum + row.amount, 0)
  );
  const buckets: Array<CajaPaymentMethod | null> = [];
  for (const row of activeRows) {
    if (!buckets.includes(row.bucket)) buckets.push(row.bucket);
  }

  const staleRows = refetching && activeTotal !== paid;

  let mode: TPaymentsBoxMode;
  if (loadError) mode = 'error';
  else if (payments === null) mode = 'loading';
  else if (rows.length === 0) mode = 'plain';
  else if (
    order.paymentStatus === OrderPaymentStatus.Paid &&
    rows.length === 1 &&
    !rows[0].reimbursed &&
    !staleRows
  ) {
    mode = 'single';
  } else mode = 'list';

  return {
    mode,
    paid,
    toPay,
    rows,
    activeCount: activeRows.length,
    activeTotal,
    buckets,
  };
}
